import { Button, Form, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { SourceLeadType } from '../types';
import ESpinner from 'components/others/Spinner';
import TableEmptyRow from 'components/others/TableEmptyRow';
import type { SourceLeadEditState } from './uiTypes';
import { SETTINGS_DIR_NAME_MAX_LENGTH } from '../types';

interface SourceLeadsTableProps {
    isLoading: boolean;
    rows: SourceLeadType[] | null;
    editingId: number | null;
    editingRow: SourceLeadEditState;
    setEditingRow: React.Dispatch<React.SetStateAction<SourceLeadEditState>>;
    onSaveEdit: (row: SourceLeadType) => void;
    onCancelEdit: () => void;
    onBeginEdit: (row: SourceLeadType) => void;
    onDelete: (row: SourceLeadType) => void;
}

const SourceLeadsTable = ({
    isLoading,
    rows,
    editingId,
    editingRow,
    setEditingRow,
    onSaveEdit,
    onCancelEdit,
    onBeginEdit,
    onDelete,
}: SourceLeadsTableProps) => {
    const { t } = useTranslation(['Settings', 'common']);
    const actionBtnClass = 'p-0 d-inline-flex align-items-center justify-content-center';
    const sanitizeDomainsInput = (value: string): string => value.replace(/[А-Яа-яЁёЇїІіЄєҐґ]/g, '');
    const formatAllowedDomains = (value?: string): string =>
        String(value || '')
            .split(',')
            .map((domain) => domain.trim())
            .filter(Boolean)
            .join(', ');

    if (isLoading || !rows) return <ESpinner />;

    return (
        <Table hover responsive className="mb-0" style={{ tableLayout: 'fixed', minWidth: '620px' }}>
            <thead>
                <tr className="bg-light">
                    <td style={{ width: '320px' }}>{t('sourceLeads.colName')}</td>
                    <td style={{ width: '180px' }}>{t('sourceLeads.colId')}</td>
                    <td style={{ width: '90px' }} className="text-end">
                        {t('sourceLeads.colActions')}
                    </td>
                </tr>
            </thead>
            <tbody>
                {rows.length === 0 ? (
                    <TableEmptyRow colSpan={3} className="text-muted py-3">
                        {t('sourceLeads.emptyList')}
                    </TableEmptyRow>
                ) : (
                    rows.map((row) => {
                        const isEdit = editingId === row.ID;
                        return (
                            <tr key={row.ID}>
                                <td>
                                    {isEdit ? (
                                        <div className="d-flex flex-column gap-2">
                                            <Form.Group className="mb-1">
                                                <label className="form-label mb-1">{t('sourceLeads.colName')}</label>
                                                <Form.Control
                                                    size="sm"
                                                    value={editingRow.sourceName}
                                                    maxLength={SETTINGS_DIR_NAME_MAX_LENGTH}
                                                    onChange={(e) =>
                                                        setEditingRow((prev) => ({ ...prev, sourceName: e.target.value }))
                                                    }
                                                />
                                            </Form.Group>
                                            <Form.Group className="mb-1">
                                                <label className="form-label mb-1">{t('sourceLeads.allowedDomains')}</label>
                                                <Form.Control
                                                    size="sm"
                                                    value={editingRow.sourceAllowDomain}
                                                    onChange={(e) =>
                                                        setEditingRow((prev) => ({
                                                            ...prev,
                                                            sourceAllowDomain: sanitizeDomainsInput(e.target.value),
                                                        }))
                                                    }
                                                    placeholder={t('sourceLeads.placeholderAllowedDomains')}
                                                />
                                            </Form.Group>
                                            <div className="text-end">
                                                <Button
                                                    onClick={onCancelEdit}
                                                    style={{ height: 24 }}
                                                    variant="secondary"
                                                    size="sm"
                                                    className="mb-1 min70px"
                                                    type="button"
                                                >
                                                    {t('common:buttons.cancel')}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="success"
                                                    style={{ height: 24 }}
                                                    className="mb-1 ms-2 min70px"
                                                    type="button"
                                                    onClick={() => onSaveEdit(row)}
                                                >
                                                    {t('common:buttons.save')}
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column">
                                            <span>{row.sourceName}</span>
                                            {formatAllowedDomains(row.sourceAllowDomain) ? (
                                                <small className="text-muted">
                                                    {t('sourceLeads.allowedDomains')}: {formatAllowedDomains(row.sourceAllowDomain)}
                                                </small>
                                            ) : null}
                                        </div>
                                    )}
                                </td>
                                <td>
                                    <code>{row.ID}</code>
                                </td>
                                <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                                    {!isEdit ? (
                                        <>
                                            <Button
                                                size="sm"
                                                variant="link"
                                                className={`text-info me-2 ${actionBtnClass}`}
                                                onClick={() => onBeginEdit(row)}
                                                title={t('common:buttons.change')}
                                            >
                                                <i className="bi bi-pencil-square"></i>
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="link"
                                                className={`text-danger ${actionBtnClass}`}
                                                onClick={() => onDelete(row)}
                                                title={t('common:buttons.delete')}
                                            >
                                                <i className="bi bi-trash"></i>
                                            </Button>
                                        </>
                                    ) : (
                                        <span className="text-muted">-</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })
                )}
            </tbody>
        </Table>
    );
};

export default SourceLeadsTable;
